import { CommonModule } from '@angular/common';
import { Component, inject, signal, computed } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  DailyFactsService,
  DailyFact,
  GenerateDailyFactVideoRequest,
} from './services/daily-facts.service';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { DailyFactsTableComponent } from './components/daily-facts-table/daily-facts-table.component';
import { DailyFactCardComponent } from './components/daily-fact-card/daily-fact-card.component';
import { DailyFactsPaginationComponent } from './components/daily-facts-pagination/daily-facts-pagination.component';
import { AdminPageShellComponent } from '../../../shared/components/admin/admin-page-shell/admin-page-shell.component';

@Component({
  selector: 'app-daily-facts',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    MatProgressSpinnerModule,
    ModalComponent,
    DailyFactsTableComponent,
    DailyFactCardComponent,
    DailyFactsPaginationComponent,
    AdminPageShellComponent,
  ],
  templateUrl: './daily-facts.component.html',
  styleUrl: './daily-facts.component.scss',
})
export class DailyFactsComponent {
  private readonly dailyFactsService = inject(DailyFactsService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  readonly generatingFactId = signal<string | null>(null);

  readonly facts = this.dailyFactsService.facts;
  readonly totalCount = this.dailyFactsService.totalCount;
  readonly page = this.dailyFactsService.page;
  readonly pageSize = this.dailyFactsService.pageSize;
  readonly isLoading = this.dailyFactsService.isLoading;
  readonly hasFacts = computed(() => this.facts().length > 0);
  readonly totalPages = computed(
    () => Math.ceil(this.totalCount() / this.pageSize()) || 1,
  );

  readonly getVideoStatusLabel = (
    status: string | null | undefined,
  ): string => {
    if (!status)
      return this.translate.instant('app.admin.dailyFacts.statusNone');
    const key = `app.admin.dailyFacts.status${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`;
    return this.translate.instant(key);
  };

  readonly getVideoStatusClass = (
    status: string | null | undefined,
  ): string => {
    return status ? status.toLowerCase() : 'none';
  };

  readonly canReviewVideo = (fact: DailyFact): boolean =>
    fact.videoStatus === 'SUCCESS' && !!fact.videoUrl;

  readonly isGenerating = (factId: string): boolean =>
    this.generatingFactId() === factId;

  videoReviewFact = signal<DailyFact | null>(null);

  generateVideo(fact: DailyFact): void {
    const req: GenerateDailyFactVideoRequest = {
      factId: fact.id,
      fact: fact.fact,
      category: fact.category || 'general',
      language: fact.language || 'en',
      date: fact.date,
    };
    this.generatingFactId.set(fact.id);
    this.dailyFactsService.generateDailyFactVideoMutation.mutate(req, {
      onSuccess: () => {
        this.toastr.success(
          this.translate.instant('app.admin.dailyFacts.videoGenerationStarted', {
            date: fact.date,
          }),
          this.translate.instant(
            'app.admin.dailyFacts.videoGenerationSuccessTitle',
          ),
        );
      },
      onError: (err: Error) => {
        const message =
          (err as { error?: { message?: string } })?.error?.message ||
          err?.message ||
          this.translate.instant('app.admin.dailyFacts.videoGenerationFailed');
        this.toastr.error(
          message,
          this.translate.instant(
            'app.admin.dailyFacts.videoGenerationErrorTitle',
          ),
        );
      },
      onSettled: () => {
        this.generatingFactId.set(null);
      },
    });
  }

  goToPage(p: number): void {
    this.dailyFactsService.setPage(
      Math.max(0, Math.min(p, this.totalPages() - 1)),
    );
  }

  reviewVideo(fact: DailyFact): void {
    if (!fact.videoUrl) return;
    this.videoReviewFact.set(fact);
  }

  closeVideoReview(): void {
    this.videoReviewFact.set(null);
  }

  /** Apply playback rate to undo stretch (e.g. 1.43 = play faster to restore natural voice). */
  setPlaybackRate(
    el: HTMLVideoElement | null,
    rate: number | undefined,
  ): void {
    if (el && rate != null && rate !== 1) {
      el.playbackRate = rate;
    }
  }
}
